import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import {
  Star, Quote, Globe, Heart, Home, User,
  ChevronLeft, ChevronRight, BarChart2, Cpu, Briefcase,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/autoplay';
import GoogleReviews from './GoogleReviews';

export const ClientTestimonialsSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.2);
  const [testimonialsSwiper, setTestimonialsSwiper] = useState<any>(null);

  const scrollPrev = useCallback(() => {
    if (testimonialsSwiper) testimonialsSwiper.slidePrev();
  }, [testimonialsSwiper]);

  const scrollNext = useCallback(() => {
    if (testimonialsSwiper) testimonialsSwiper.slideNext();
  }, [testimonialsSwiper]);

  const testimonials = [
    {
      quote: "Fahad Al Tamimi Law Firm guided us seamlessly through our Saudi market entry. Their deep understanding of MISA regulations and local business culture was invaluable. What sets them apart is their responsiveness and commitment to our success.",
      author: "Michael Chen",
      position: "Regional Director, APAC",
      company: "European Tech Multinational",
      industry: "Technology",
      icon: User,
      rating: 5,
    },
    {
      quote: "We needed urgent assistance with a complex labor dispute involving senior executives. The team's expertise in Saudi labor law and their strategic approach led to a favorable settlement. Their professionalism exceeded our expectations.",
      author: "Nora Al Khalifa",
      position: "Chief Legal Officer",
      company: "Leading Saudi Industrial Group",
      industry: "Manufacturing",
      icon: User,
      rating: 5,
    },
    {
      quote: "Our corporate restructuring required intricate knowledge of Saudi commercial law and Vision 2030 regulations. The firm delivered a comprehensive solution within tight deadlines. Their fixed-fee pricing model provided budget certainty.",
      author: "James Robertson",
      position: "Managing Partner",
      company: "International Private Equity Fund",
      industry: "Investment",
      icon: User,
      rating: 5,
    },
    {
      quote: "Exceptional service and deep local expertise. They handled our cross-border acquisition flawlessly, navigating complex regulations and cultural nuances.",
      author: "Sarah Williams",
      position: "VP Legal",
      company: "Global Logistics Group",
      industry: "Transportation",
      icon: User,
      rating: 5,
    },
  ];

  const clientLogos = [
    { name: 'Fortune 500', category: 'Energy & Utilities' },
    { name: 'Global Tech', category: 'Technology Sector' },
    { name: 'Investment Fund', category: 'Private Equity' },
    { name: 'Saudi Corp', category: 'Manufacturing' },
    { name: 'Healthcare', category: 'Medical Services' },
    { name: 'Real Estate', category: 'Development' },
  ];

  const clientIcons = [
    <BarChart2 size={24} />,
    <Cpu size={24} />,
    <Briefcase size={24} />,
    <Globe size={24} />,
    <Heart size={24} />,
    <Home size={24} />
  ];

  return (
    <>
    <GoogleReviews/>
   <section className="relative pt-0 pb-20 sm:pb-24 lg:pb-28 overflow-hidden w-full" style={{background:'#faf9f7', display:'none'}}>

   {/* <section className="relative pt-20 pb-20 sm:pb-24 lg:pb-28 overflow-hidden w-full" style={{background:'#faf9f7'}}> */}

      <style>{`
        .testimonials-swiper .swiper-wrapper {
          align-items: stretch !important;
        }
        .testimonials-swiper .swiper-slide {
          height: auto !important;
          display: flex !important;
        }
        @media (max-width: 640px) {
          .testimonials-swiper {
            padding: 0.5rem 2rem !important;
          }
        }
      `}</style>

      {/* Gold accent lines */}
      {/* <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#c3a565]/40 to-transparent" /> */}
      {/* <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#c3a565]/40 to-transparent" /> */}
      
      {/* Soft decorative blur (light version for white bg) */}
      {/* <div className="absolute top-1/4 right-0 w-[800px] h-[600px] bg-[#c3a565]/5 blur-[120px] rounded-full" /> */}

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" ref={ref}>

        <div className="mb-16 sm:mb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-4 mb-4"
          >
            <div className="h-px w-16 bg-[#c3a565]" />
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[#c3a565]" strokeWidth={2} fill="#c3a565" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#c3a565]" style={{ fontWeight: 600 }}>
                Client Success
              </span>
            </div>
            <div className="h-px w-16 bg-[#c3a565]" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[#2b2b2a] text-3xl sm:text-4xl lg:text-5xl mb-4"
            style={{ fontWeight: 600 }}
          >
            Trusted by Leading Organizations
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[#2b2b2a]/70 max-w-3xl mx-auto"
            style={{ fontWeight: 300, fontSize: '1rem' }}
          >
            Delivering exceptional legal outcomes for corporations, investors, and institutions across Saudi Arabia
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative mb-16 sm:mb-20"
        >
          <button
            onClick={scrollPrev}
            aria-label="Previous testimonial"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full border border-[#c3a565]/30 bg-white text-[#c3a565] hover:border-[#c3a565] hover:bg-[#c3a565]/10 transition-all duration-300 cursor-pointer shadow-md"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2} />
          </button>
          <button
            onClick={scrollNext}
            aria-label="Next testimonial"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full border border-[#c3a565]/30 bg-white text-[#c3a565] hover:border-[#c3a565] hover:bg-[#c3a565]/10 transition-all duration-300 cursor-pointer shadow-md"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2} />
          </button>

          <Swiper
            onSwiper={setTestimonialsSwiper}
            modules={[Autoplay]}
            loop={true}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              640: {
                slidesPerView: 1.2,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 24,
              },
            }}
            className="testimonials-swiper"
          >
            {testimonials.map((testimonial, index) => {
              const Icon = testimonial.icon;
              return (
                <SwiperSlide key={index} className="h-full">
                  <div className="bg-white border border-[#c3a565]/20 rounded-2xl p-6 sm:p-7 h-full flex flex-col transition-all duration-500 hover:border-[#c3a565]/60 hover:shadow-[0_10px_40px_rgba(195,165,101,0.12)]">
                    <div className="mb-4">
                      <Quote className="w-10 h-10 text-[#c3a565]/30" strokeWidth={1.5} />
                    </div>
                    <p className="text-[#2b2b2a]/80 leading-relaxed mb-6 flex-1" style={{ fontWeight: 300, fontSize: '0.95rem' }}>
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-start gap-3 pt-5 border-t border-[#c3a565]/10">
                      <div className="w-10 h-10 rounded-full bg-[#c3a565]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#c3a565]" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-[#2b2b2a] mb-0.5" style={{ fontWeight: 500, fontSize: '0.95rem' }}>
                          {testimonial.author}
                        </div>
                        <div className="text-[#c3a565] text-sm mb-1" style={{ fontWeight: 400 }}>
                          {testimonial.position}
                        </div>
                        <div className="text-[#2b2b2a]/50 text-xs mb-2" style={{ fontWeight: 300 }}>
                          {testimonial.company} • {testimonial.industry}
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 text-[#c3a565] fill-[#c3a565]" strokeWidth={0} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="max-w-7xl mx-auto px-4"
        >
          <div className="text-center mb-10">
            <p
              className="inline-block px-4 py-1 text-xs tracking-[0.3em] uppercase rounded-full
                         bg-[#c3a565]/10 text-[#c3a565]"
              style={{ fontWeight: 600 }}
            >
              Our Clients
            </p>
          </div>

          <div
            className="relative rounded-3xl border border-[#c3a565]/15 
                       bg-gradient-to-br from-white via-[#faf9f7] to-white
                       backdrop-blur-none p-5 sm:px-10 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-6 auto-rows-fr items-stretch">
              {clientLogos.map((client, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: index * 0.06 }}
                  className="group h-full"
                >
                  <div
                    className="relative rounded-2xl h-full
                               p-5
                               bg-gradient-to-b from-[#2b2b2a]/5 to-transparent
                               border border-[#c3a565]/15
                               flex flex-col
                               transition-all duration-500
                               group-hover:border-[#c3a565]/40
                               group-hover:shadow-[0_0_30px_rgba(195,165,101,0.1)]"
                  >
                    <div className="h-10 flex items-center justify-center text-[#c3a565] mb-3">
                      {clientIcons[index]}
                    </div>
                    <h3 className="text-[#2b2b2a] text-base font-bold text-center leading-tight line-clamp-2 h-[40px] mb-2">
                      {client.name}
                    </h3>
                    <p className="text-[#2b2b2a]/50 text-xs tracking-wide text-center h-[32px] leading-snug">
                      {client.category}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <p className="text-[#2b2b2a]/60 text-sm flex items-center gap-2 justify-center" style={{ fontWeight: 300 }}>
                <Globe size={16} className="text-[#c3a565]" style={{ minWidth: '20px', height: 'auto' }} />
                Trusted by multinational enterprises, institutional investors, and Saudi-based businesses.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
        </>
  );
};